import { registerPlugin } from "@capacitor/core";
import { useEffect } from "react";

/**
 * JS bridge for the native ScreenshotGuard plugin. Real implementations
 * are in ios/App/App/ScreenshotGuardPlugin.swift and
 * android/app/src/main/java/com/assemblytickets/app/ScreenshotGuardPlugin.java.
 *
 * The web fallback is a no-op: browsers can't block screenshots, and no
 * one is redeeming a bar drink from a laptop anyway.
 */
export interface ScreenshotGuardPlugin {
  /** enabled=true blocks screenshots + screen recording for the app window. */
  setEnabled(options: { enabled: boolean }): Promise<void>;
}

export const ScreenshotGuard = registerPlugin<ScreenshotGuardPlugin>(
  "ScreenshotGuard",
  {
    web: () => ({
      setEnabled: async () => {
        // Web has no equivalent; screenshots are OS-level and no browser
        // gives us the hook. The redemption proof screen falls back to
        // the live tickers + auto-expire on desktop.
      },
    }),
  },
);

/**
 * React hook: guard screenshots while the component is mounted, release
 * on unmount. Use on any screen that shows a redemption proof, wallet QR,
 * etc.
 *
 *   function ProofPage() {
 *     useScreenshotGuard();
 *     return <div>...</div>;
 *   }
 */
export function useScreenshotGuard(): void {
  useEffect(() => {
    ScreenshotGuard.setEnabled({ enabled: true }).catch(() => {
      // Native plugin missing (older TestFlight build) or web fallback,
      // silently degrade. The redemption still works, screenshot block
      // just isn't there.
    });
    return () => {
      ScreenshotGuard.setEnabled({ enabled: false }).catch(() => {});
    };
  }, []);
}
